import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, RouterModule } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { AppMaterialModule } from '../../../app-material.module';
import { AuthService } from '../../../shared/services/auth.service';
import { LoaderComponent } from '../../../core/loader/loader.component';
import { AppGeneralsModuleslModule } from '../../../app-generals-modules.module';
import { NavigationService } from '../../../shared/services/navigation.service';
import { Subject } from 'rxjs';
import { takeUntil, delay, filter } from 'rxjs/operators';
import { FontesTrafegoService } from '../../../shared/services/fontes-trafego/fontes-trafego.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { GitbookAiEmbedComponent } from '../../../shared/components/gitbook-ai-embed/gitbook-ai-embed.component';
import { MixPanelService } from '../../../shared/services/mix-panel/mix-panel.service';
import { ThemeService } from '../../../shared/services/theme.service';
import { isHubRedirectActive, resolveHubHomeUrl } from '../../../shared/utils/hub-redirect';
import { V2ShellHeaderComponent } from './components/v2-shell-header/v2-shell-header.component';
import { V2ShellSidebarComponent } from './components/v2-shell-sidebar/v2-shell-sidebar.component';
import { V2ShellLayoutStore } from './services/v2-shell-layout.store';
import { V2ShellNavService } from './services/v2-shell-nav.service';
import { CampaignTickerComponent } from '../../../shared/components/campaign-ticker/campaign-ticker.component';
import { PageBreadcrumbComponent } from '../../../shared/components/page-breadcrumb/page-breadcrumb.component';
import { PageBreadcrumbItem } from '../../../shared/components/page-breadcrumb/page-breadcrumb.types';
import { buildBreadcrumbsFromRoute } from '../../../shared/utils/page-breadcrumb.utils';
import { REDE_ADS_INDISPONIVEL_URL } from '../../../shared/guards/rede-ads/rede-ads-ativa.guard';
import { MatDialog } from '@angular/material/dialog';
import {
    FORCED_REDE_ADS_DIALOG_ID,
    FontesTrafegoPageComponent,
} from '../../fontes-trafego/fontes-trafego-page/fontes-trafego-page.component';

@Component({
    selector: 'app-main-container-v2',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        AppMaterialModule,
        LoaderComponent,
        AppGeneralsModuleslModule,
        GitbookAiEmbedComponent,
        TranslocoPipe,
        CampaignTickerComponent,
        V2ShellHeaderComponent,
        V2ShellSidebarComponent,
        PageBreadcrumbComponent,
    ],
    providers: [V2ShellLayoutStore, V2ShellNavService],
    templateUrl: './main-container-v2.component.html',
    styleUrls: ['./main-container-v2.component.scss'],
})
export class MainContainerV2Component implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('sidenav') sidenav!: MatSidenav;
    @ViewChild(MatSidenavContainer) sidenavContainer?: MatSidenavContainer;
    @ViewChild(GitbookAiEmbedComponent) gitbookAiEmbed?: GitbookAiEmbedComponent;

    private destroy$ = new Subject<void>();

    sidenavOpen = false;
    currentBreakpoint = 'desktop';
    nomeCliente = '';
    public isAdm = false;
    breadcrumbItems: PageBreadcrumbItem[] = [];

    private readonly MOBILE_BREAKPOINT = '(max-width: 767.98px)';
    private readonly DESKTOP_BREAKPOINT = '(min-width: 768px)';
    public habilitarDashboard = false;
    public habilitarGerenciadorAnuncios = false;
    private unlimitedRedeAdsResetStarted = false;

    constructor(
        public layoutStore: V2ShellLayoutStore,
        private breakpointObserver: BreakpointObserver,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private authService: AuthService,
        private navigationService: NavigationService,
        private mixPanelService: MixPanelService,
        public themeService: ThemeService,
        private dialog: MatDialog,
        private fontesTrafegoService: FontesTrafegoService,
    ) {
        this.setupInitialBreakpoints();
    }

    get ratoeiraHubUrl(): string {
        if (!isHubRedirectActive()) {
            return '';
        }
        return resolveHubHomeUrl();
    }

    get hideShellSidenav(): boolean {
        return this.router.url.split('?')[0].startsWith(REDE_ADS_INDISPONIVEL_URL);
    }

    ngOnInit(): void {
        this.initUserInfoSubscriptions();
        this.setupResponsiveLayout();
        this.initMenuCollapseListener();
        this.initRouteCollapseListener();
        this.updateBreadcrumbsFromRoute();
    }

    private initUserInfoSubscriptions(): void {
        this.authService.profileHydrated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadUserInfo();
        });
        this.authService.ensureProfileHydrated().subscribe(() => {
            this.loadUserInfo();
        });
    }

    private initMenuCollapseListener(): void {
        this.navigationService.collapseMenu$
            .pipe(takeUntil(this.destroy$), delay(100))
            .subscribe((shouldCollapse: boolean) => {
                if (this.layoutStore.isDesktop) {
                    this.layoutStore.isCollapsed = shouldCollapse;
                    this.syncSidenavContentMargins();
                }
            });
    }

    private initRouteCollapseListener(): void {
        this.router.events
            .pipe(
                takeUntil(this.destroy$),
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            )
            .subscribe((event) => {
                this.handleRouteCollapse(event.urlAfterRedirects);
                this.updateBreadcrumbsFromRoute();
                queueMicrotask(() => this.syncSidenavContentMargins());
            });
    }

    private updateBreadcrumbsFromRoute(): void {
        this.breadcrumbItems = buildBreadcrumbsFromRoute(this.activatedRoute);
    }

    ngAfterViewInit(): void {
        this.layoutStore.registerCloseSidenav(() => {
            if (this.sidenav?.opened) {
                this.sidenav.close();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupFeatures(): void {
        this.refreshPlanFeatureFlags();
    }

    private refreshPlanFeatureFlags(): void {
        this.habilitarDashboard = true;
        this.habilitarGerenciadorAnuncios = true;
    }

    private handleRouteCollapse(url: string): void {
        const isProjetoCentral = /^\/v2\/projetos\/[0-9]+(\/|$)/.test(url);

        if (this.layoutStore.isDesktop && isProjetoCentral) {
            this.layoutStore.isCollapsed = true;
            this.navigationService.triggerMenuCollapse(true);
        }
    }

    toggleAiSideSheet(): void {
        this.gitbookAiEmbed?.toggle();
    }

    onHeaderMenuToggle(): void {
        if (this.layoutStore.isMobile) {
            this.sidenav?.toggle();
            return;
        }

        this.toggleCollapse();
    }

    private loadUserInfo(): void {
        const cliente = this.authService.cliente;
        if (cliente) {
            const nome = typeof cliente.name === 'string' ? cliente.name.trim() : '';
            this.nomeCliente = nome || 'Usuário';
        }

        this.isAdm = this.authService.verificaAdminPermissao();
        this.resetUnlimitedRedeAdsIfNeeded();
        this.openForcedRedeAdsDialogIfNeeded();
        this.refreshPlanFeatureFlags();
    }

    private resetUnlimitedRedeAdsIfNeeded(): void {
        if (this.unlimitedRedeAdsResetStarted || !this.authService.needsUnlimitedRedeAdsReset()) {
            return;
        }

        this.unlimitedRedeAdsResetStarted = true;
        this.fontesTrafegoService
            .updateRedeAdsAtivas(null)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    if (!result?.success) {
                        this.unlimitedRedeAdsResetStarted = false;
                        return;
                    }

                    if (this.authService.cliente) {
                        this.authService.cliente.rede_ads_ativas = null;
                    }
                    this.fontesTrafegoService.redeAdsAtivasUpdated$.next();
                },
                error: () => {
                    this.unlimitedRedeAdsResetStarted = false;
                },
            });
    }

    private openForcedRedeAdsDialogIfNeeded(): void {
        if (!this.authService.needsForcedRedeAdsSelection()) {
            return;
        }
        if (this.dialog.getDialogById(FORCED_REDE_ADS_DIALOG_ID)) {
            return;
        }

        this.dialog.open(FontesTrafegoPageComponent, {
            id: FORCED_REDE_ADS_DIALOG_ID,
            disableClose: true,
            data: { forceSelection: true },
            width: '920px',
            maxWidth: '96vw',
            autoFocus: false,
            panelClass: 'forced-rede-ads-dialog',
        });
    }

    private setupInitialBreakpoints(): void {
        this.layoutStore.isMobile = this.breakpointObserver.isMatched(this.MOBILE_BREAKPOINT);
        this.layoutStore.isDesktop = this.breakpointObserver.isMatched(this.DESKTOP_BREAKPOINT);
    }

    private updateResponsiveLayout(): void {
        const isMobileMatch = this.breakpointObserver.isMatched(this.MOBILE_BREAKPOINT);
        const isDesktopMatch = this.breakpointObserver.isMatched(this.DESKTOP_BREAKPOINT);

        this.layoutStore.isMobile = isMobileMatch;
        this.layoutStore.isDesktop = isDesktopMatch;

        if (this.layoutStore.isMobile) {
            this.currentBreakpoint = 'mobile';
        } else {
            this.currentBreakpoint = 'desktop';
            this.layoutStore.isDesktop = true;
        }

        if (this.layoutStore.isDesktop && this.sidenavOpen) {
            this.sidenavOpen = false;
        }

        this.handleBreakpointChange();
    }

    private setupResponsiveLayout(): void {
        this.breakpointObserver
            .observe([this.MOBILE_BREAKPOINT, this.DESKTOP_BREAKPOINT])
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateResponsiveLayout();
            });
    }

    get isSidenavOpened(): boolean {
        return this.layoutStore.isDesktop || this.sidenavOpen;
    }

    private handleBreakpointChange(): void {
        if (this.layoutStore.isMobile) {
            this.layoutStore.isCollapsed = false;
            this.sidenav?.close();
            return;
        }

        this.sidenav?.open();
    }

    toggleCollapse(): void {
        if (this.layoutStore.isDesktop) {
            this.layoutStore.isCollapsed = !this.layoutStore.isCollapsed;
            this.syncSidenavContentMargins();
        }
    }

    private syncSidenavContentMargins(): void {
        queueMicrotask(() => this.sidenavContainer?.updateContentMargins());
    }

    onSidenavClosed(): void {
        this.sidenavOpen = false;
    }

    onSidenavOpened(): void {
        this.sidenavOpen = true;
    }

    getSidenavMode(): 'over' | 'side' {
        return this.layoutStore.isMobile ? 'over' : 'side';
    }

    toggleVersion(): void {
        const routeBefore = this.router.url || '';

        this.mixPanelService.track('goto_versao_antiga', {
            page: 'main-container-v2',
            from_version: 'v2',
            to_version: 'v1',
            route_before: routeBefore,
        });

        localStorage.setItem('system_version', 'v1');
        this.router.navigate(['/painel']);
    }

    handleDesconto(): void {
        window.open('https://ratoeiraads.com.br/#planos', '_blank');
    }
}
